
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { Loader2 } from "lucide-react";
import { useCsrf } from "@/hooks/use-csrf";
import { useInputValidation } from "@/hooks/use-input-validation";

interface SignInFormProps {
  onForgotPassword: () => void;
}

export function SignInForm({ onForgotPassword }: SignInFormProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const email = useInputValidation({
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    preventXss: true
  });
  const password = useInputValidation({
    required: true,
    minLength: 8,
    preventXss: true
  });
  const navigate = useNavigate();
  const { csrfToken, refreshCsrfToken } = useCsrf();

  // Get a fresh CSRF token when the component loads
  useEffect(() => {
    refreshCsrfToken();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate inputs
    if (email.error || password.error) {
      toast.error(email.error?.message || password.error?.message);
      return;
    }
    
    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.value,
        password: password.value,
        options: {
          captchaToken: csrfToken // Using CSRF token for additional security
        }
      });
      
      if (error) throw error;
      
      // Refresh CSRF token after successful login
      refreshCsrfToken();
      
      // Navigate first to ensure the UI responds quickly
      navigate("/dashboard", { replace: true });
      
      // Show success toast after navigation has started
      toast.success(t('sign_in_success') || "Successfully signed in!");
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Hidden CSRF token field */}
      <input type="hidden" name="csrfToken" value={csrfToken} />
      
      <div className="space-y-2">
        <Label htmlFor="email">{t('email_address')}</Label>
        <Input
          id="email"
          type="email"
          placeholder="name@example.com"
          value={email.value}
          onChange={(e) => email.handleChange(e.target.value)}
          onBlur={email.handleBlur}
          required
        />
        {email.error && email.isTouched && (
          <p className="text-sm text-destructive">{email.error.message}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="password">{t('password')}</Label>
        <Input
          id="password"
          type="password"
          value={password.value}
          onChange={(e) => password.handleChange(e.target.value)}
          onBlur={password.handleBlur}
          required
        />
        {password.error && password.isTouched && (
          <p className="text-sm text-destructive">{password.error.message}</p>
        )}
      </div>
      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('processing') || "Signing in..."}
          </>
        ) : (
          t('sign_in')
        )}
      </Button>
      <Button
        type="button"
        variant="link"
        className="w-full"
        onClick={onForgotPassword}
      >
        {t('forgot_password')}
      </Button>
    </form>
  );
}
